/**
 *
 * @module telemetryManager
 * @see https://github.bamtech.co/sdk-doc/spec-sdk/blob/master/specs/feature_overviews/telemetry.md
 *
 */

import Check from 'check-types';
import Logging from './../../logging/entry';
import Services from './../../services/entry';
import Token from './../../token/entry';
import EventBuffer from './eventBuffer';

const Util = Services.Util;
const TelemetryManagerConfiguration = Services.Configuration.TelemetryManagerConfiguration;
const TelemetryClient = Services.Internal.Telemetry.TelemetryClient;
const TokenManager = Token.TokenManager;

const InvalidArgumentException = Services.Exception.InvalidArgumentException;

const {
    instanceStrict
} = Check.assert;

/**
 *
 * @class TelemetryManager
 * @desc Provides a manager that can be used to access post telemetry events.
 *
 */
export default class TelemetryManager {

    /**
     *
     * @access private
     * @param {SDK.Services.Configuration.TelemetryManagerConfiguration} telemetryManagerConfiguration
     * @param {SDK.Token.TokenManager} tokenManager
     * @param {SDK.Services.Internal.Telemetry.TelemetryClient} telemetryClient
     * @param {SDK.Logging.Logger} [logger=Logging]
     * @throws {InvalidArgumentException}
     *
     */
    constructor(telemetryManagerConfiguration, tokenManager, telemetryClient, logger = Logging) {


        const errorMessage = {
            ctor: 'TelemetryManager(telemetryManagerConfiguration, tokenManager, telemetryClient, logger)',
            get telemetryManagerConfiguration() {
                return `${this.ctor} needs TelemetryManagerConfiguration instance`;
            },
            get tokenManager() {
                return `${this.ctor} needs SDK.Token.TokenManager instance`;
            },
            get telemetryClient() {
                return `${this.ctor} needs telemetryClient instance`;
            }
        };

        instanceStrict(telemetryManagerConfiguration, TelemetryManagerConfiguration, errorMessage.telemetryManagerConfiguration, InvalidArgumentException); // eslint-disable-line
        instanceStrict(tokenManager, TokenManager, errorMessage.tokenManager, InvalidArgumentException);
        instanceStrict(telemetryClient, TelemetryClient, errorMessage.telemetryClient, InvalidArgumentException);

        /**
         *
         * @access private
         * @type {SDK.Logging.Logger}
         *
         */
        this.logger = logger;

        /**
         *
         * @access private
         * @type {SDK.Internal.Telemetry.EventBuffer}
         * @desc used for posting stream sample events
         * @note if streamSampleConfiguration isn't provided telemetryExtrasMap will use bufferConfigurationDefault
         *
         */
        this.streamSampleBuffer = new EventBuffer(
            Util.getSafe(() => telemetryManagerConfiguration.extras.streamSampleConfiguration),
            tokenManager,
            Util.getSafe(() => telemetryClient.config.endpoints.postEvent),
            telemetryClient,
            logger
        );

        /**
         *
         * @access private
         * @type {SDK.Internal.Telemetry.EventBuffer}
         * @desc used for posting dust events
         *
         */
        this.eventBuffer = new EventBuffer(
            Util.getSafe(() => telemetryManagerConfiguration.extras.bufferConfigurationDefault),
            tokenManager,
            Util.getSafe(() => telemetryClient.config.endpoints.dustEvent),
            telemetryClient,
            logger
        );

        /**
         *
         * @access private
         * @type {SDK.Internal.Telemetry.EventBuffer}
         * @desc used for user activity tracking events.
         *
         */
        this.trackingBuffer = new EventBuffer(
            Util.getSafe(() => telemetryManagerConfiguration.extras.bufferConfigurationDefault),
            tokenManager,
            Util.getSafe(() => telemetryClient.config.endpoints.dustEvent),
            telemetryClient,
            logger
        );

        this.logger.log(TelemetryManager.name, 'Created.');

    }
}



// WEBPACK FOOTER //
// ./src/internal/telemetry/telemetryManager.js